import React, { useCallback } from 'react';
import Button from '@atlaskit/button';
import LoadingButton from '@atlaskit/button/loading-button';
import Form, { ErrorMessage, Field } from '@atlaskit/form';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import ModalDialog, { ModalBody, ModalFooter } from '@atlaskit/modal-dialog';
import { Box, Flex, Stack, Text, xcss } from '@atlaskit/primitives';
import TextField from '@atlaskit/textfield';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { PriorityModalHeader } from '../../../common/modal-header';
import type { Priority } from '../../../common/types';
import { messages } from './messages';

const ORDER = 'order';

type Props = {
	priority: Priority;
	total: number;
	onClose: () => void;
	onConfirm: (priorityId: string, order: number) => Promise<void>;
};

export const MovePriorityModal = ({ priority, total, onClose, onConfirm }: Props) => {
	const { formatMessage } = useIntl();

	const handleSubmit = useCallback(
		async (data: { [ORDER]: string }) => {
			await onConfirm(priority?.id, Number(data[ORDER]));
			if (fg('jsw_priority_modals_admin_page')) {
				onClose && onClose();
			}
		},
		[onConfirm, priority, onClose],
	);

	const onValidateOrder = useCallback(
		(value: string | undefined) => {
			const number = Number(value);
			if (Number.isNaN(number) || !Number.isSafeInteger(number) || number < 1 || number > total) {
				return formatMessage(messages.orderFieldError, {
					maximumOrderValue: total,
				});
			}
			return undefined;
		},
		[formatMessage, total],
	);

	return (
		// Todo remove ModalDialog in cleanup of jsw_priority_modals_admin_page
		<ModalDialog width="small" onClose={onClose}>
			<PriorityModalHeader
				testId="admin-pages-priorities.ui.priority-modals.move-modal.priority-table-modal-title"
				onClose={onClose}
				title={formatMessage(messages.movePriorityModalHeader)}
			/>
			<Box>
				<Form<{ [ORDER]: string }> onSubmit={handleSubmit}>
					{({ formProps, submitting }) => (
						<form id="move-priority-modal" {...formProps}>
							<ModalBody>
								<Flex direction="column" gap="space.100" xcss={descriptionStyles}>
									<Text as="p">
										{formatMessage(messages.movePriorityModalRequiredFieldsMessage)}
									</Text>
									<Text as="p">
										{formatMessage(messages.movePriorityModalBody, {
											priorityName: priority.name,
										})}
									</Text>
								</Flex>
								<Field
									name="order"
									label={formatMessage(messages.orderFieldLabel)}
									isRequired
									validate={onValidateOrder}
								>
									{({ fieldProps, error }) => (
										<Stack>
											<TextField
												{...fieldProps}
												name={ORDER}
												type="number"
												min={1}
												max={total}
												onChange={(value) => fieldProps.onChange(value)}
												placeholder={formatMessage(messages.orderFieldPlaceholder, {
													maximumOrderValue: total,
												})}
											/>
											{error && <ErrorMessage>{error}</ErrorMessage>}
										</Stack>
									)}
								</Field>
							</ModalBody>
							<ModalFooter>
								<Button appearance="subtle" onClick={onClose} type="button">
									{formatMessage(messages.cancelButton)}
								</Button>
								<LoadingButton appearance="primary" type="submit" isLoading={submitting}>
									{formatMessage(messages.confirmButton)}
								</LoadingButton>
							</ModalFooter>
						</form>
					)}
				</Form>
			</Box>
		</ModalDialog>
	);
};

const descriptionStyles = xcss({
	paddingBottom: 'space.100',
});
